import { Badge, Box, Code, Collapse, Divider, HStack, IconButton, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { JinjaEnv, JinjaVariable, JinjaFunction } from "build-ts/jinja";
import { DarwinAgentType } from "build-ts/common";

// Variable Card Component - Clean and minimal
interface VariableCardProps {
    variable: JinjaVariable;
}

const VariableCard: React.FC<VariableCardProps> = ({ variable }) => {
    return (
        <Box 
            p={3} 
            bg="white" 
            borderRadius="md" 
            border="1px solid" 
            borderColor="gray.200"
            _hover={{ borderColor: "gray.300", shadow: "sm" }}
            transition="all 0.2s"
        >
            <HStack spacing={2} mb={2}>
                <Code fontSize="sm" colorScheme="gray" fontWeight="medium">{variable.name || ""}</Code>
                <Badge size="sm" colorScheme="gray" variant="subtle">{variable.type || "Any"}</Badge>
            </HStack>

            {variable.description && (
                <Text fontSize="xs" color="gray.600" mb={2}>
                {variable.description}
            </Text>
            )}

            {variable.example && (
                <Box p={2} bg="gray.50" borderRadius="sm" border="1px solid" borderColor="gray.100">
                    <Code fontSize="xs" color="gray.700" bg="transparent">
                    {variable.example}
                </Code>
            </Box>
            )}
        </Box>
    );
};

// Function Card Component - Clean and minimal
interface FunctionCardProps {
    func: JinjaFunction;
}

const FunctionCard: React.FC<FunctionCardProps> = ({ func }) => {
    return (
        <Box 
            p={3} 
            bg="white" 
            borderRadius="md" 
            border="1px solid" 
            borderColor="gray.200"
            _hover={{ borderColor: "gray.300", shadow: "sm" }}
            transition="all 0.2s"
        >
            <HStack spacing={2} mb={2}>
                <Code fontSize="sm" colorScheme="gray" fontWeight="medium">{func.name || ""}()</Code>
                <Badge size="sm" colorScheme="gray" variant="subtle">{func.return_type || "Any"}</Badge>
            </HStack>

            {func.description && (
                <Text fontSize="xs" color="gray.600" mb={2}>
                {func.description}
            </Text>
            )}

            {func.parameters && func.parameters.length > 0 && (
                <Text fontSize="xs" color="gray.500" mb={2}>
                    Parameters: {func.parameters.join(", ")}
                    </Text>
            )}

            {func.example && (
                <Box p={2} bg="gray.50" borderRadius="sm" border="1px solid" borderColor="gray.100">
                    <Code fontSize="xs" color="gray.700" bg="transparent">
                    {func.example}
                </Code>
            </Box>
            )}
        </Box>
    );
};

// Object Group Component - Clean expandable for classes
interface ObjectGroupProps {
    objectName: string;
    objectType: string;
    description?: string;
    properties: JinjaVariable[];
    methods: JinjaFunction[];
}

const ObjectGroup: React.FC<ObjectGroupProps> = ({ objectName, objectType, description, properties, methods }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [arePropertiesExpanded, setArePropertiesExpanded] = useState(false);
    const [areMethodsExpanded, setAreMethodsExpanded] = useState(false);

    return (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
            {/* Main Class Header */}
            <Box
                p={3}
                bg="gray.50"
                cursor="pointer"
                onClick={() => setIsExpanded(!isExpanded)}
                _hover={{ bg: "gray.100" }}
                transition="background 0.2s"
            >
                <HStack spacing={3} justify="space-between">
                    <HStack spacing={2}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={0}>{objectType}</Text>
                    </HStack>
                    <IconButton
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                        icon={isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                        size="xs"
                        variant="ghost"
                    />
                </HStack>
                {description && (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                        {description}
                    </Text>
                )}
            </Box>

            {/* Nested Properties and Methods */}
            <Collapse in={isExpanded}>
                <Box p={3} bg="white">
                    <VStack spacing={3} align="stretch">
                        {/* Properties Section */}
                        {properties && properties.length > 0 && (
                            <Box>
                                <HStack
                                    p={2}
                                    cursor="pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setArePropertiesExpanded(!arePropertiesExpanded);
                                    }}
                                    _hover={{ bg: "gray.50" }}
                                    borderRadius="sm"
                                >
                                    <IconButton
                                        aria-label={arePropertiesExpanded ? "Collapse" : "Expand"}
                                        icon={arePropertiesExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                        size="xs"
                                        variant="ghost"
                                    />
                                    <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={0}>
                                        Properties ({properties.length})
                                    </Text>
                                </HStack>
                                <Collapse in={arePropertiesExpanded}>
                                    <Box pl={6} pt={2}>
                                        <VStack spacing={2} align="stretch">
                                            {properties.map((variable) => (
                            <VariableCard key={variable.name} variable={variable} />
                        ))}
                                        </VStack>
                </Box>
            </Collapse>
        </Box>
                        )}

                        {/* Methods Section */}
                        {methods && methods.length > 0 && (
                            <Box>
                                <HStack
                                    p={2}
                cursor="pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setAreMethodsExpanded(!areMethodsExpanded);
                                    }}
                                    _hover={{ bg: "gray.50" }}
                                    borderRadius="sm"
                                >
                    <IconButton
                                        aria-label={areMethodsExpanded ? "Collapse" : "Expand"}
                                        icon={areMethodsExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                        size="xs"
                        variant="ghost"
                    />
                                    <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={0}>
                                        Methods ({methods.length})
                                    </Text>
                </HStack>
                                <Collapse in={areMethodsExpanded}>
                                    <Box pl={6} pt={2}>
                                        <VStack spacing={2} align="stretch">
                                            {methods.map((func) => (
                            <FunctionCard key={func.name} func={func} />
                        ))}
                                        </VStack>
                                    </Box>
                                </Collapse>
                    </Box>
                        )}
                    </VStack>
                </Box>
            </Collapse>
        </Box>
    );
};

// Section Header Component
const SectionHeader: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
    <Box mb={3}>
        <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={1}>
            {title}
        </Text>
        <Text fontSize="xs" color="gray.600">
            {subtitle}
        </Text>
        <Divider mt={2} />
    </Box>
);

interface DocModalProps {
    isDocsOpen: boolean;
    onDocsClose: () => void;
    isLoadingDoc: boolean;
    availableVariables: JinjaEnv | null;
    agentType?: DarwinAgentType;
}

const DocModal: React.FC<DocModalProps> = ({isDocsOpen, onDocsClose, isLoadingDoc, availableVariables, agentType}) => {
    const [areObjectsExpanded, setAreObjectsExpanded] = useState(false);
    const [areUtilityFunctionsExpanded, setAreUtilityFunctionsExpanded] = useState(false);
    
    // Extract data from JinjaEnv
    const objects = availableVariables?.objects || [];
    const utilityFunctions = availableVariables?.utility_functions || [];
    
    // Filter agent input variables based on agent type
    const agentInputVariables: JinjaVariable[] = (() => {
        if (!agentType || !availableVariables) return [];
        
        const agentVars = availableVariables.agent_input_variables?.find(av => av.agent_type === agentType);
        return agentVars?.input_variables || [];
    })();

    return (
        <Modal isOpen={isDocsOpen} onClose={onDocsClose} size="6xl">
            <ModalOverlay />
            <ModalContent bg="gray.50">
                <ModalHeader borderBottom="1px solid" borderColor="gray.200" bg="white">
                    <Text fontSize="lg" fontWeight="semibold">Template Documentation</Text>
                </ModalHeader>
            <ModalCloseButton />
                <ModalBody p={6}>
                <VStack spacing={6} align="stretch">
                {isLoadingDoc ? (
                            <Box p={8} textAlign="center">
                                <Text color="gray.500" fontSize="sm">Loading documentation...</Text>
                    </Box>
                        ) : (!objects || objects.length === 0) && (!utilityFunctions || utilityFunctions.length === 0) && (!agentInputVariables || agentInputVariables.length === 0) ? (
                            <Box p={8} textAlign="center">
                                <Text color="gray.500" fontSize="sm">No documentation available</Text>
                    </Box>
                ) : (
                    <>
                                {/* Agent Input Variables Section */}
                                {agentInputVariables && agentInputVariables.length > 0 && (
                                    <Box>
                                        <SectionHeader 
                                            title="Agent Input Variables" 
                                            subtitle="Variables provided when the agent executes" 
                                        />
                                        <VStack spacing={2} align="stretch">
                                            {agentInputVariables.map((variable: JinjaVariable) => (
                                                <VariableCard key={variable.name} variable={variable} />
                                            ))}
                                        </VStack>
                                    </Box>
                                )}

                                {/* Objects Section */}
                                {objects && objects.length > 0 && (
                                    <Box>
                                        <SectionHeader 
                                            title="Classes & Objects" 
                                            subtitle="MKG classes with properties and methods" 
                                        />
                                        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
                                            <Box
                                                p={3}
                                                bg="gray.50"
                                                cursor="pointer"
                                                onClick={() => setAreObjectsExpanded(!areObjectsExpanded)}
                                                _hover={{ bg: "gray.100" }}
                                                transition="background 0.2s"
                                            >
                                                <HStack spacing={3} justify="space-between">
                                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={0}>
                                                        Available Classes ({objects.length})
                                                    </Text>
                                                    <IconButton
                                                        aria-label={areObjectsExpanded ? "Collapse" : "Expand"}
                                                        icon={areObjectsExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                                        size="xs"
                                                        variant="ghost"
                                                    />
                                                </HStack>
                                            </Box>
                                            <Collapse in={areObjectsExpanded}>
                                                <Box p={3}>
                                                    <VStack spacing={2} align="stretch">
                                                        {objects.map((obj) => (
                                                            <ObjectGroup
                                                                key={obj.name}
                                                                objectName={obj.name || ""}
                                                                objectType={obj.type || ""}
                                                                description={obj.description}
                                                                properties={obj.object_variables || []}
                                                                methods={obj.object_functions || []}
                                                            />
                                                        ))}
                                                    </VStack>
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    </Box>
                        )}

                                {/* Utility Functions Section */}
                                {utilityFunctions && utilityFunctions.length > 0 && (
                                    <Box>
                                        <SectionHeader 
                                            title="Utility Functions" 
                                            subtitle="Standalone helper functions" 
                                        />
                                        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
                                            <Box
                                                p={3}
                                                bg="gray.50"
                                                cursor="pointer"
                                                onClick={() => setAreUtilityFunctionsExpanded(!areUtilityFunctionsExpanded)}
                                                _hover={{ bg: "gray.100" }}
                                                transition="background 0.2s"
                                            >
                                                <HStack spacing={3} justify="space-between">
                                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={0}>
                                                        Available Functions ({utilityFunctions.length})
                                                    </Text>
                                                    <IconButton
                                                        aria-label={areUtilityFunctionsExpanded ? "Collapse" : "Expand"}
                                                        icon={areUtilityFunctionsExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                                        size="xs"
                                                        variant="ghost"
                                                    />
                                                </HStack>
                                            </Box>
                                            <Collapse in={areUtilityFunctionsExpanded}>
                                                <Box p={3}>
                                                    <VStack spacing={2} align="stretch">
                                                        {utilityFunctions.map((func) => (
                                                            <FunctionCard key={func.name} func={func} />
                                                        ))}
                                                    </VStack>
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    </Box>
                                )}

                                {/* Quick Reference */}
                                <Box p={4} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200" mt={4}>
                                    <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={3}>
                                        Quick Reference
                                </Text>
                                    <VStack spacing={1.5} align="stretch">
                                        <HStack spacing={2} fontSize="xs">
                                            <Code colorScheme="gray" fontSize="xs">{"{{ variable }}"}</Code>
                                            <Text color="gray.600" mb={0}>Access a variable</Text>
                                        </HStack>
                                        <HStack spacing={2} fontSize="xs">
                                            <Code colorScheme="gray" fontSize="xs">{"{{ object.property }}"}</Code>
                                            <Text color="gray.600" mb={0}>Access object property</Text>
                                        </HStack>
                                        <HStack spacing={2} fontSize="xs">
                                            <Code colorScheme="gray" fontSize="xs">{"{{ object.method() }}"}</Code>
                                            <Text color="gray.600" mb={0}>Call object method</Text>
                                        </HStack>
                                        <HStack spacing={2} fontSize="xs">
                                            <Code colorScheme="gray" fontSize="xs">{"{{ function(arg) }}"}</Code>
                                            <Text color="gray.600" mb={0}>Call utility function</Text>
                                        </HStack>
                                        <HStack spacing={2} fontSize="xs">
                                            <Code colorScheme="gray" fontSize="xs">{"{% for item in list %}...{% endfor %}"}</Code>
                                            <Text color="gray.600" mb={0}>Loop through list</Text>
                                        </HStack>
                                    </VStack>
                                </Box>
                            </>
                        )}
                </VStack>
            </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default DocModal;
